import UserGroupIcon from '@assets/images/icons/user-group.svg?react'
import { UNIT_LABELS } from '@components/ArtBoard/Estimator/constants'
import { Badge, Loading, Select, Switch } from '@lib/components'
import type {
  EstimateCurrency,
  EstimateUnits,
} from '@octopus-do/octopus-project-core'
import { Link as LinkIcon } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  changeEstimatesCurrency,
  changeEstimatesHidden,
  changeEstimatesTaxEnabled,
  changeEstimatesUnits,
} from '@store/reducers/estimates/actions'
import type { AppState } from '@store/types'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { usePdf, useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { ESTIMATE_PERMISSION } from '@/permissions/project/definitions'
import { GlobalActions, selectIsLoading } from '@/store'
import { MODAL_UPGRADE_PLAN } from '../../../../App/Modal/constants'
import { gerCurrencyOptions } from './helper'
import css from './Settings.module.scss'

type Props = {
  copyUrl: string
  closeDropdown?: () => void
}

export const Settings: React.FC<Props> = ({ copyUrl, closeDropdown }) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const currency = useSelector(
    migrationProjectSelector.getProjectEstimateCurrency
  )
  const estimateUnits = useSelector(
    migrationProjectSelector.getProjectEstimateUnits
  )
  const taxEnabled = useSelector(
    migrationProjectSelector.getProjectEstimateTaxEnabled
  )
  const privateEstimate = useSelector(
    migrationProjectSelector.getProjectEstimateSettingPrivate
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const artboardDisabled = useArtBoardDisabled()

  const {
    hasAccess: hasAccessPrivate,
    hasNotAvailableAccessBadge: hasNotAvailableAccessBadgePrivate,
    badgeType: badgeTypePrivate,
  } = useProjectAccess(ESTIMATE_PERMISSION.MAKE_PRIVATE)
  const {
    hasAccess: hasAccessPdf,
    hasNotAvailableAccessBadge: hasNotAvailableAccessBadgePdf,
    badgeType: badgeTypePdf,
  } = useProjectAccess(ESTIMATE_PERMISSION.SAVE_AS_PDF)

  const { submit: exportPdf } = usePdf('export_pdf', {
    isStickyNotesEnabled: false,
    isColorLegendEnabled: false,
    isLogoAndTitleEnabled: true,
  })

  const pdfExportPending = useSelector(selectIsLoading('export_pdf'))

  const handleCopy = () => {
    dispatch(GlobalActions.copy({ value: copyUrl }))
  }

  const handleExportPdf = async () => {
    if (!hasAccessPdf) {
      return
    }

    await exportPdf()
    closeDropdown?.()
  }

  const handleBadgeClick = () => {
    dispatch(
      openModal({
        modalType: MODAL_UPGRADE_PLAN,
        modalProps: {
          planType: 'team',
        },
      })
    )
  }

  const hideSavePdf = true

  if (isReadOnly) {
    return (
      <>
        {!hideSavePdf && (
          <>
            <div className={css.item}>
              <button
                className={css.pdfBtn}
                disabled={pdfExportPending || !hasAccessPdf}
                onClick={handleExportPdf}
              >
                {pdfExportPending ? (
                  <Loading color="gray" type="small" variant="two" />
                ) : (
                  <i className="fa fa-arrow-to-bottom" />
                )}
                Save as PDF
                {hasNotAvailableAccessBadgePdf && <Badge type={badgeTypePdf} />}
              </button>
            </div>

            <div className={css.divider} />
          </>
        )}
        <div className={css.item}>
          <button className={css.copyButton} onClick={handleCopy}>
            <LinkIcon /> Copy link
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {!hideSavePdf && (
        <>
          <div className={css.item}>
            <button
              className={css.pdfBtn}
              disabled={pdfExportPending || !hasAccessPdf}
              onClick={handleExportPdf}
            >
              {pdfExportPending ? (
                <Loading color="gray" type="small" variant="two" />
              ) : (
                <i className="fa fa-arrow-to-bottom" />
              )}
              Save as PDF
              {hasNotAvailableAccessBadgePdf && <Badge type={badgeTypePdf} />}
            </button>
          </div>

          <div className={css.divider} />
        </>
      )}

      <div className={css.item}>
        <button className={css.copyButton} onClick={handleCopy}>
          <LinkIcon /> Copy link
        </button>
      </div>

      <div className={css.item}>
        <div className={css.label}>
          <UserGroupIcon className={css.userGroupIcon} />
          Hide from public
        </div>
        {!hasNotAvailableAccessBadgePrivate ? (
          <Switch
            checked={privateEstimate}
            disabled={!hasAccessPrivate || artboardDisabled}
            onChange={(hidden: boolean) => {
              if (hasAccessPrivate) {
                if (hidden) {
                  sendGtmEvent(
                    GtmTriggerName.ESTIM_HIDE,
                    'Toggle to hide estimate from public'
                  )
                }

                dispatch(changeEstimatesHidden({ hidden }))
              }
            }}
          />
        ) : (
          <Badge type={badgeTypePrivate} onClick={handleBadgeClick} />
        )}
      </div>

      <div className={css.divider} />

      <div className={css.item}>
        <div className={css.label}>Units</div>
        <Select
          type="select"
          options={Object.keys(UNIT_LABELS).map((key) => ({
            value: key,
            label: UNIT_LABELS[key],
          }))}
          selected={estimateUnits}
          disabled={artboardDisabled}
          successIcon
          position="bottom-end"
          onChange={(value) => {
            dispatch(changeEstimatesUnits({ units: value as EstimateUnits }))
          }}
        />
      </div>

      <div className={css.item}>
        <div className={css.label}>Currency</div>
        <Select
          type="select"
          options={gerCurrencyOptions()}
          selected={currency}
          disabled={artboardDisabled}
          successIcon
          position="bottom-end"
          withSearch
          listMinWidth={240}
          onChange={(currency) => {
            dispatch(
              changeEstimatesCurrency({
                currency: currency as EstimateCurrency,
              })
            )
          }}
        />
      </div>

      <div className={css.item}>
        <div className={css.label}>Show tax</div>
        <Switch
          checked={taxEnabled}
          disabled={artboardDisabled}
          onChange={(taxEnabled: boolean) => {
            dispatch(changeEstimatesTaxEnabled({ taxEnabled }))
          }}
        />
      </div>
    </>
  )
}
